import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { apiUrl } from "../../utils/backendUrl";
import { socket } from "../../routes/IsLoggedIn";
import { useMiniChat, miniChatBus } from "../../context/MiniChatContext";
import MiniChatView from "./MiniChatView";
import useVoiceNoteRecorder from "./useVoiceNoteRecorder";
import {
  getRealtimeMessageSenderId,
  shouldRouteMessageToMiniChat,
} from "../../utils/miniChatRealtime";

const MINI_CHAT_TEXTAREA_MIN_HEIGHT = 36;
const MINI_CHAT_TEXTAREA_MAX_HEIGHT = 112;

export default function MiniChat({ chat, rightPosition }) {
  const { currentUser, closeMiniChat, toggleMinimize, setConversationId } =
    useMiniChat();

  const { id: chatId, user: friend, conversationId, minimized } = chat;

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [copiedMessageKey, setCopiedMessageKey] = useState("");
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const messageAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);
  const initialScrollLockRef = useRef(false);
  const copyResetTimeoutRef = useRef(null);
  const {
    isSupported: voiceNotesSupported,
    isRecording: isVoiceNoteRecording,
    isProcessing: isVoiceNoteProcessing,
    elapsedMs: voiceNoteElapsedMs,
    previewFile: pendingVoiceNoteFile,
    previewUrl: pendingVoiceNoteUrl,
    previewDurationMs: pendingVoiceNoteDurationMs,
    startRecording: startVoiceNoteRecording,
    stopRecording: stopVoiceNoteRecording,
    cancelRecording: cancelVoiceNoteRecording,
    clearPreview: clearVoiceNotePreview,
  } = useVoiceNoteRecorder({
    onError: (message) => setError(message),
  });
  const hasPendingVoiceNote = Boolean(pendingVoiceNoteFile);

  const resizeInput = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    input.style.height = "0px";
    const nextHeight = Math.min(input.scrollHeight, MINI_CHAT_TEXTAREA_MAX_HEIGHT);
    input.style.height = `${Math.max(nextHeight, MINI_CHAT_TEXTAREA_MIN_HEIGHT)}px`;
    input.style.overflowY =
      input.scrollHeight > MINI_CHAT_TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  const friendName =
    `${friend.first_name || friend.firstName || ""} ${friend.last_name || friend.lastName || ""}`.trim() ||
    friend.username ||
    "User";

  const mergeMessages = useCallback((existing, incoming) => {
    const list = Array.isArray(incoming) ? incoming : [incoming];
    const merged = [...existing];
    const seen = new Set(
      merged.map(
        (message) => message?._id || `${message?.sender}-${message?.createdAt}`,
      ),
    );

    list.forEach((message) => {
      const key = message?._id || `${message?.sender}-${message?.createdAt}`;
      if (!seen.has(key)) {
        merged.push(message);
        seen.add(key);
      }
    });

    return merged;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (minimized) return;
    const el = messageAreaRef.current;
    if (!el) return;

    const run = () => {
      el.scrollTop = el.scrollHeight;
    };

    run();
    window.requestAnimationFrame(() => {
      run();
      window.setTimeout(run, 60);
      window.setTimeout(run, 200);
      window.setTimeout(run, 600);
      window.setTimeout(run, 1200);
    });
  }, [minimized]);

  const lockBottomOnOpen = useCallback(() => {
    initialScrollLockRef.current = true;
    scrollToBottom();
    window.setTimeout(() => {
      initialScrollLockRef.current = false;
    }, 1500);
  }, [scrollToBottom]);

  // scrollToBottom (and so lockBottomOnOpen) changes identity with
  // `minimized`; the init / subscribe effects below read it through this ref
  // so minimising or expanding the window never re-creates the conversation,
  // refetches the history or re-subscribes to the bus.
  const lockBottomOnOpenRef = useRef(lockBottomOnOpen);
  useEffect(() => {
    lockBottomOnOpenRef.current = lockBottomOnOpen;
  }, [lockBottomOnOpen]);

  const friendId = String(friend._id || friend.id || "");

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const handleMessageCopied = useCallback((messageKey) => {
    setCopiedMessageKey(messageKey);
    if (copyResetTimeoutRef.current) {
      window.clearTimeout(copyResetTimeoutRef.current);
    }
    copyResetTimeoutRef.current = window.setTimeout(() => {
      setCopiedMessageKey("");
      copyResetTimeoutRef.current = null;
    }, 1800);
  }, []);

  // ─── File management helpers ──────────────────────────────────────

  const addFiles = useCallback((files) => {
    const fileArray = Array.from(files);
    clearVoiceNotePreview();
    setSelectedFiles((prev) => [...prev, ...fileArray].slice(0, 5));

    fileArray.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setFilePreviews((prev) => [
          ...prev,
          { name: file.name, url, type: file.type },
        ]);
      } else {
        setFilePreviews((prev) => [
          ...prev,
          { name: file.name, url: null, type: file.type, size: file.size },
        ]);
      }
    });
  }, [clearVoiceNotePreview]);

  const removeFile = useCallback((index) => {
    setSelectedFiles((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
    setFilePreviews((prev) => {
      const updated = [...prev];
      if (updated[index]?.url) URL.revokeObjectURL(updated[index].url);
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const clearFiles = useCallback(() => {
    filePreviews.forEach((p) => {
      if (p.url) URL.revokeObjectURL(p.url);
    });
    setSelectedFiles([]);
    setFilePreviews([]);
  }, [filePreviews]);

  // ─── 1. Resolve conversation + load messages ─────────────────────

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        setLoading(true);
        setError("");

        const { data: convData } = await axios.post(
          apiUrl("/api/v1/messages/conversations"),
          { participantId: friendId },
          { withCredentials: true },
        );
        const conv = convData?.data?.conversation;
        if (!conv?._id) throw new Error("Could not create conversation");
        if (!mounted) return;

        setConversationId(chatId, conv._id);

        const { data: msgData } = await axios.get(
          apiUrl(
            `/api/v1/messages/conversations/${conv._id}/messages?page=1&limit=20`,
          ),
          { withCredentials: true },
        );
        if (!mounted) return;
        const msgs = msgData?.data?.messages || [];
        const queuedMessages = miniChatBus.drainQueuedMessages(chatId);
        setMessages(mergeMessages(msgs, queuedMessages));
        lockBottomOnOpenRef.current();

        try {
          await axios.patch(
            apiUrl(`/api/v1/messages/conversations/${conv._id}/read`),
            {},
            { withCredentials: true },
          );
        } catch (_) {}
      } catch (e) {
        if (mounted)
          setError(
            e?.response?.data?.message || e?.message || "Failed to load chat",
          );
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();
    return () => {
      mounted = false;
    };
    // Re-run only for a different peer (setConversationId / mergeMessages are
    // stable callbacks); scroll behaviour goes through lockBottomOnOpenRef.
  }, [chatId, friendId, setConversationId, mergeMessages]);

  // ─── 2. Auto-scroll on new messages ──────────────────────────────

  useEffect(() => {
    if (!loading && !minimized) {
      scrollToBottom();
    }
  }, [messages, minimized, loading, scrollToBottom]);

  useEffect(() => {
    resizeInput();
  }, [draft, resizeInput]);

  // ─── 3. Focus input when opened / un-minimized ───────────────────

  useEffect(() => {
    if (!minimized && !loading) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [minimized, loading]);

  // ─── 4. Subscribe to incoming messages via miniChatBus ───────────

  useEffect(() => {
    const handler = (newMessage) => {
      const senderId = getRealtimeMessageSenderId(newMessage);
      const matchesOpenChat = shouldRouteMessageToMiniChat({
        message: newMessage,
        friendId,
        conversationId,
      });

      if (!matchesOpenChat) {
        return;
      }

      setMessages((prev) => mergeMessages(prev, newMessage));
      lockBottomOnOpenRef.current();

      if (senderId === friendId && conversationId) {
        axios
          .patch(
            apiUrl(`/api/v1/messages/conversations/${conversationId}/read`),
            {},
            { withCredentials: true },
          )
          .catch(() => {});
      }
    };

    miniChatBus.subscribe(chatId, handler);
    return () => miniChatBus.unsubscribe(chatId);
  }, [chatId, friendId, conversationId, mergeMessages]);

  // ─── 5. Listen for message status updates ────────────────────────

  useEffect(() => {
    if (!socket) return;

    const handleRead = (data) => {
      if (!data?.messageIds) return;
      const ids = new Set(data.messageIds);
      setMessages((prev) =>
        prev.map((m) => (ids.has(m._id) ? { ...m, status: "read" } : m)),
      );
    };

    const handleDelivered = (data) => {
      if (!data?.messageId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m._id === data.messageId && m.status === "sent"
            ? { ...m, status: "delivered" }
            : m,
        ),
      );
    };

    socket.on("messages_read", handleRead);
    socket.on("message_delivered", handleDelivered);
    return () => {
      socket.off("messages_read", handleRead);
      socket.off("message_delivered", handleDelivered);
    };
  }, []);

  // ─── 6. Send message (text and/or files) ──────────────────────────

  const handleSend = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      const content = draft.trim();
      const hasFiles = selectedFiles.length > 0;
      const hasVoiceNote = Boolean(pendingVoiceNoteFile);
      if ((!content && !hasFiles && !hasVoiceNote) || !conversationId || sending) {
        return;
      }

      try {
        setSending(true);
        setUploadProgress(0);

        let data;

        if (hasVoiceNote) {
          const formData = new FormData();
          formData.append("attachments", pendingVoiceNoteFile);
          formData.append("messageType", "voice_note");
          formData.append(
            "voiceNoteDurationMs",
            `${Math.max(0, Number(pendingVoiceNoteDurationMs) || 0)}`,
          );

          const resp = await axios.post(
            apiUrl(`/api/v1/messages/conversations/${conversationId}/messages`),
            formData,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
            },
          );
          data = resp.data;
        } else if (hasFiles) {
          const formData = new FormData();
          if (content) formData.append("content", content);
          selectedFiles.forEach((file) => formData.append("attachments", file));

          const resp = await axios.post(
            apiUrl(`/api/v1/messages/conversations/${conversationId}/messages`),
            formData,
            {
              withCredentials: true,
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: (evt) => {
                const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
                setUploadProgress(pct);
              },
            },
          );
          data = resp.data;
        } else {
          const resp = await axios.post(
            apiUrl(`/api/v1/messages/conversations/${conversationId}/messages`),
            { content },
            { withCredentials: true },
          );
          data = resp.data;
        }

        const msg = data?.data?.message;
        if (msg) {
          setMessages((prev) => mergeMessages(prev, msg));
          lockBottomOnOpen();
        }
        setDraft("");
        clearFiles();
        clearVoiceNotePreview();
      } catch (e2) {
        setError(e2?.response?.data?.message || "Failed to send");
        setTimeout(() => setError(""), 3000);
      } finally {
        setSending(false);
        setUploadProgress(0);
      }
    },
    [
      draft,
      selectedFiles,
      conversationId,
      sending,
      clearFiles,
      clearVoiceNotePreview,
      mergeMessages,
      lockBottomOnOpen,
      pendingVoiceNoteDurationMs,
      pendingVoiceNoteFile,
    ],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleAttachmentLayout = useCallback(() => {
    if (initialScrollLockRef.current) {
      scrollToBottom();
    }
  }, [scrollToBottom]);

  // ─── 7. Clipboard paste handler ──────────────────────────────────

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") {
          const file = items[i].getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        addFiles(files);
      }
    },
    [addFiles],
  );

  // ─── 8. Drag and drop handlers ───────────────────────────────────

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setDragging(false);

      const files = e.dataTransfer?.files;
      if (files?.length > 0) addFiles(files);
    },
    [addFiles],
  );

  // ─── 9. File picker handler ──────────────────────────────────────

  const handleFileSelect = useCallback(
    (e) => {
      const files = e.target?.files;
      if (files?.length > 0) addFiles(files);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [addFiles],
  );

  return (
    <MiniChatView
      cancelVoiceNoteRecording={cancelVoiceNoteRecording}
      chatId={chatId}
      clearVoiceNotePreview={clearVoiceNotePreview}
      closeMiniChat={closeMiniChat}
      conversationId={conversationId}
      copiedMessageKey={copiedMessageKey}
      currentUser={currentUser}
      draft={draft}
      dragging={dragging}
      endRef={endRef}
      error={error}
      fileInputRef={fileInputRef}
      filePreviews={filePreviews}
      friend={friend}
      friendName={friendName}
      handleAttachmentLayout={handleAttachmentLayout}
      handleDragEnter={handleDragEnter}
      handleDragLeave={handleDragLeave}
      handleDragOver={handleDragOver}
      handleDrop={handleDrop}
      handleFileSelect={handleFileSelect}
      handleKeyDown={handleKeyDown}
      handleMessageCopied={handleMessageCopied}
      handlePaste={handlePaste}
      handleSend={handleSend}
      hasPendingVoiceNote={hasPendingVoiceNote}
      inputRef={inputRef}
      isVoiceNoteProcessing={isVoiceNoteProcessing}
      isVoiceNoteRecording={isVoiceNoteRecording}
      loading={loading}
      messageAreaRef={messageAreaRef}
      messages={messages}
      minimized={minimized}
      pendingVoiceNoteDurationMs={pendingVoiceNoteDurationMs}
      pendingVoiceNoteUrl={pendingVoiceNoteUrl}
      removeFile={removeFile}
      rightPosition={rightPosition}
      selectedFiles={selectedFiles}
      sending={sending}
      setDraft={setDraft}
      startVoiceNoteRecording={startVoiceNoteRecording}
      stopVoiceNoteRecording={stopVoiceNoteRecording}
      toggleMinimize={toggleMinimize}
      uploadProgress={uploadProgress}
      voiceNoteElapsedMs={voiceNoteElapsedMs}
      voiceNotesSupported={voiceNotesSupported}
    />
  );
}
